import { useMemo, useState } from "react";
import { buildNailPlan, FINGERS, HANDS, nailKey, planCompletion, firstOpenNail } from "../lib/nailPlan.js";
import { Button } from "./Button.jsx";

export function NailPlanViewer({
  design,
  workProgress = {},
  mode = "preview",
  onSetStatus,
}) {
  const plan = useMemo(() => buildNailPlan(design || {}), [design]);
  const completion = planCompletion(plan, workProgress);
  const initial = firstOpenNail(plan, workProgress);
  const [activeHand, setActiveHand] = useState(initial?.hand || "left");
  const [activeKey, setActiveKey] = useState(initial?.key || nailKey("left", "thumb"));
  const activeNail = plan.find((nail) => nail.key === activeKey) || plan[0];

  if (!design) return null;

  function selectHand(handId) {
    const next = plan.find((nail) => nail.hand === handId && workProgress?.[nail.key]?.status !== "done")
      || plan.find((nail) => nail.hand === handId)
      || plan[0];
    setActiveHand(handId);
    if (next) setActiveKey(next.key);
  }

  return (
    <section style={{ display: "grid", gap: 14 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>
            10-nail execution map
          </div>
          <div style={{ color: "var(--text)", fontWeight: 800, marginTop: 2 }}>
            {completion.completed}/{completion.total} nails completed
          </div>
        </div>
        <div aria-label={`${completion.percent}% complete`} style={{
          width: 170,
          height: 8,
          borderRadius: 999,
          background: "var(--bg-soft)",
          overflow: "hidden",
          border: "1px solid var(--border)",
        }}>
          <div style={{ width: `${completion.percent}%`, height: "100%", background: "linear-gradient(90deg, var(--mauve), var(--gold))" }} />
        </div>
      </div>

      <div role="tablist" aria-label="Choose hand" style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 8,
        padding: 4,
        border: "1px solid var(--border)",
        borderRadius: 14,
        background: "var(--bg-soft)",
      }}>
        {HANDS.map((hand) => (
          <button
            key={hand.id}
            type="button"
            role="tab"
            aria-selected={activeHand === hand.id}
            onClick={() => selectHand(hand.id)}
            style={{
              border: "1px solid transparent",
              background: activeHand === hand.id ? "var(--bg-elevated)" : "transparent",
              color: "var(--text)",
              borderRadius: 10,
              minHeight: 44,
              padding: "10px 12px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 900,
              boxShadow: activeHand === hand.id ? "var(--shadow-sm)" : "none",
            }}
          >
            {hand.label}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 12 }}>
        {HANDS.map((hand) => (
          <article
            key={hand.id}
            aria-label={hand.label}
            style={{
              border: activeHand === hand.id ? "2px solid var(--mauve)" : "1px solid var(--border)",
              background: activeHand === hand.id ? "rgba(196,135,154,0.12)" : "var(--bg-soft)",
              color: "var(--text)",
              borderRadius: 14,
              padding: 14,
              textAlign: "left",
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
              <div style={{ fontWeight: 900 }}>{hand.label}</div>
              <button
                type="button"
                onClick={() => selectHand(hand.id)}
                style={{
                  border: "1px solid var(--border)",
                  background: activeHand === hand.id ? "var(--mauve)" : "var(--bg-elevated)",
                  color: activeHand === hand.id ? "#fff" : "var(--text)",
                  borderRadius: 999,
                  padding: "6px 10px",
                  minHeight: 34,
                  fontSize: 11,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Select
              </button>
            </div>
            <HandDiagram handId={hand.id} activeKey={activeKey} workProgress={workProgress} onPick={(key) => {
              setActiveHand(hand.id);
              setActiveKey(key);
            }} />
          </article>
        ))}
      </div>

      {activeNail && (
        <article style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          gap: 14,
          padding: 14,
          border: "1px solid var(--border)",
          borderRadius: 14,
          background: "var(--bg-elevated)",
        }}>
          <div style={{
            minHeight: 190,
            borderRadius: 12,
            border: "1px solid var(--border)",
            background: activeNail.image_url
              ? `linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.28)), url(${activeNail.image_url}) center/cover`
              : "var(--bg-soft)",
            display: "flex",
            alignItems: "flex-end",
            padding: 12,
            color: "#fff",
          }}>
            <div style={{ fontWeight: 900, textShadow: "0 1px 8px rgba(0,0,0,.4)" }}>{activeNail.fingerLabel}</div>
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h4 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 20, color: "var(--text)" }}>
                  {activeNail.title}
                </h4>
                <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 3 }}>
                  Estimated {activeNail.estimatedMinutes} min · {design.difficulty || "standard detail"}
                </div>
              </div>
              <StatusPill status={workProgress?.[activeNail.key]?.status || "not_started"} />
            </div>

            {activeNail.colors?.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                {activeNail.colors.map((color, index) => (
                  <span key={`${color}-${index}`} title={color} style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: color,
                    border: "1px solid var(--border-strong)",
                  }} />
                ))}
              </div>
            )}

            <ol style={{ margin: "14px 0 0", paddingLeft: 18, color: "var(--text)", lineHeight: 1.5, fontSize: 13 }}>
              {activeNail.method.map((step, index) => <li key={index}>{step}</li>)}
            </ol>

            {activeNail.products?.length > 0 && (
              <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {activeNail.products.map((product, index) => (
                  <span key={index} style={{
                    padding: "4px 9px",
                    borderRadius: 999,
                    background: "rgba(196,135,154,0.13)",
                    color: "var(--text-accent)",
                    border: "1px solid var(--border-strong)",
                    fontSize: 11,
                    fontWeight: 700,
                  }}>
                    {product}
                  </span>
                ))}
              </div>
            )}

            {mode === "work" && onSetStatus && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
                <Button size="sm" label="Start nail" variant="outline" onClick={() => onSetStatus(activeNail.key, "in_progress")} />
                <Button size="sm" label="Mark done" variant="primary" onClick={() => {
                  onSetStatus(activeNail.key, "done");
                  const currentIndex = plan.findIndex((nail) => nail.key === activeNail.key);
                  const next = plan.slice(currentIndex + 1).find((nail) => workProgress?.[nail.key]?.status !== "done");
                  if (next) {
                    setActiveHand(next.hand);
                    setActiveKey(next.key);
                  }
                }} />
              </div>
            )}
          </div>
        </article>
      )}
    </section>
  );
}

function HandDiagram({ handId, activeKey, workProgress, onPick }) {
  const fingers = handId === "right" ? [...FINGERS].reverse() : FINGERS;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(44px, 1fr))", gap: 8, alignItems: "end" }}>
      {fingers.map((finger) => {
        const key = nailKey(handId, finger.id);
        const status = workProgress?.[key]?.status || "not_started";
        const active = activeKey === key;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            aria-label={`${handId === "left" ? "Left" : "Right"} ${finger.label}`}
            onClick={(event) => {
              event.stopPropagation();
              onPick(key);
            }}
            style={{
              display: "grid",
              gap: 5,
              justifyItems: "center",
              alignContent: "end",
              color: "var(--text)",
              cursor: "pointer",
              background: active ? "rgba(196,135,154,0.14)" : "transparent",
              border: active ? "1px solid rgba(196,135,154,0.5)" : "1px solid transparent",
              borderRadius: 12,
              padding: "8px 4px",
              minWidth: 0,
              minHeight: 106,
            }}
          >
            <span style={{
              width: finger.id === "thumb" ? "clamp(25px, 42%, 34px)" : "clamp(22px, 38%, 29px)",
              height: finger.id === "thumb" ? 54 : 66,
              borderRadius: "999px 999px 16px 16px",
              border: active ? "2px solid var(--mauve)" : "1px solid var(--border-strong)",
              background: status === "done"
                ? "linear-gradient(180deg, var(--gold), #9A6A10)"
                : status === "in_progress"
                  ? "linear-gradient(180deg, var(--mauve), var(--mauve-deep))"
                  : "linear-gradient(180deg, #f7f0f3, #d8cbd1)",
              boxShadow: active ? "0 0 0 4px rgba(196,135,154,0.16)" : "none",
            }} />
            <span style={{ fontSize: 10, fontWeight: 900, lineHeight: 1.1 }}>{finger.id === "middle" ? "Mid" : finger.label.split(" ")[0]}</span>
          </button>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  const label = status === "done" ? "Done" : status === "in_progress" ? "In progress" : "Not started";
  return (
    <span style={{
      alignSelf: "flex-start",
      padding: "5px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 900,
      color: status === "done" ? "#111" : "var(--text)",
      background: status === "done" ? "var(--gold)" : status === "in_progress" ? "rgba(196,135,154,0.18)" : "var(--bg-soft)",
      border: "1px solid var(--border)",
    }}>
      {label}
    </span>
  );
}
